import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'next-i18next';

import isNotNull from 'src/utils/isNotNull';
import type { Ad } from 'src/types/__generated__/graphql';
import Typography from 'src/components/ui/Typography';
import Icon from 'src/components/ui/Icon';

export enum HomeAttributeIconBarVariants {
    SMALL = 'SMALL',
    MEDIUM = 'MEDIUM',
    LARGE = 'LARGE',
}

// Icons
const RoomIcon = <Icon size="1rem" name="Room" />;
const AreaIcon = <Icon size="1rem" name="Area" />;
const BedRoomIcon = <Icon size="1rem" name="BedRoom" />;

export type Props = {
    ad: Partial<Ad>;
    homePropertiesDensity: HomeAttributeIconBarVariants;
};
const HomeAttributeIconBar: React.FC<Props> = ({ ad, homePropertiesDensity }) => {
    const { t } = useTranslation(['common', 'ad']);

    switch (homePropertiesDensity) {
        case HomeAttributeIconBarVariants.SMALL:
            return (
                <>
                    {isNotNull(ad.roomNumber) && (
                        <Box mr={0.5} display="flex" alignItems="center">
                            {RoomIcon}
                            <Box ml={0.5}>
                                <Typography variant="body2">{ad.roomNumber}</Typography>
                            </Box>
                        </Box>
                    )}
                    {isNotNull(ad.livingArea) && (
                        <Box mr={0.5} display="flex" alignItems="center">
                            {AreaIcon}
                            <Box ml={0.5}>
                                <Typography
                                    variant="body2"
                                    dangerouslySetInnerHTML={{
                                        __html: t('ad:livingArea', {
                                            livingArea: ad.livingArea,
                                        }),
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                    {/* surfaceArea is the surface for parkings / lands */}
                    {isNotNull(ad.surfaceArea) && (
                        <Box display="flex" alignItems="center">
                            {AreaIcon}
                            <Box ml={0.5}>
                                <Typography
                                    variant="body2"
                                    dangerouslySetInnerHTML={{
                                        __html: t('ad:surfaceArea', {
                                            surfaceArea: ad.surfaceArea,
                                        }),
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </>
            );
        case HomeAttributeIconBarVariants.LARGE:
            return (
                <>
                    {isNotNull(ad.roomNumber) && (
                        <Box display="flex" alignItems="center">
                            <Box>{RoomIcon}</Box>
                            <Typography data-target-id="count-rooms" sx={{ fontSize: '0.875rem' }}>
                                {t('ad:countRoomsShort', {
                                    count: ad.roomNumber,
                                }).replace(' ', '')}
                            </Typography>
                        </Box>
                    )}
                    {isNotNull(ad.bedroomNumber) && (
                        <Box display="flex" alignItems="center">
                            <Box>{BedRoomIcon}</Box>
                            <Typography data-target-id="count-bedrooms" sx={{ fontSize: '0.875rem' }}>
                                {t('ad:countBedRoomsShort', {
                                    count: ad.bedroomNumber,
                                }).replace(' ', '')}
                            </Typography>
                        </Box>
                    )}
                    {isNotNull(ad.livingArea) && (
                        <Box display="flex" alignItems="center">
                            <Box>{AreaIcon}</Box>
                            <Box>
                                <Typography
                                    sx={{ fontSize: '0.875rem' }}
                                    data-target-id="living-area"
                                    dangerouslySetInnerHTML={{
                                        __html: t('ad:livingArea', {
                                            livingArea: ad.livingArea,
                                        }),
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                    {isNotNull(ad.surfaceArea) && (
                        <Box display="flex" alignItems="center">
                            {AreaIcon}
                            <Box ml={0.5}>
                                <Typography variant="body2" data-target-id="surface-area" sx={{ fontSize: '0.875rem' }}>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: t('ad:surfaceArea', {
                                                surfaceArea: ad.surfaceArea,
                                            }),
                                        }}
                                    />
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {ad.type === 'rent' && ad.furnished && (
                        <Box display="flex" alignItems="center">
                            <Box>
                                <Icon size="1rem" name="Sofa" />
                            </Box>
                            <Typography data-target-id="furnished" ml={0.5}>
                                {t('ad:furnished')}
                            </Typography>
                        </Box>
                    )}
                </>
            );
        default:
            return (
                <Box display="flex">
                    {isNotNull(ad.bedroomNumber) && (
                        <Box mr={1} display="flex" alignItems="center" width="max-content">
                            {BedRoomIcon}
                            <Box ml={0.5}>
                                <Typography variant="body2">
                                    {t('ad:countBedRoomsShort', {
                                        count: ad.bedroomNumber,
                                    })}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {isNotNull(ad.roomNumber) && (
                        <Box mr={1} display="flex" alignItems="center" width="max-content">
                            {RoomIcon}
                            <Box ml={0.5}>
                                <Typography variant="body2">
                                    {t('ad:countRoomsShort', {
                                        count: ad.roomNumber,
                                    })}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {isNotNull(ad.livingArea) && (
                        <Box mr={1} display="flex" alignItems="center" width="max-content">
                            {AreaIcon}
                            <Box ml={0.5}>
                                <Typography
                                    variant="body2"
                                    dangerouslySetInnerHTML={{
                                        __html: t('ad:livingArea', {
                                            livingArea: ad.livingArea,
                                        }),
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                    {/* surfaceArea is the surface for parkings / lands */}
                    {isNotNull(ad.surfaceArea) && (
                        <Box display="flex" alignItems="center">
                            {AreaIcon}
                            <Box ml={0.5}>
                                <Typography
                                    variant="body2"
                                    dangerouslySetInnerHTML={{
                                        __html: t('ad:surfaceArea', {
                                            surfaceArea: ad.surfaceArea,
                                        }),
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            );
    }
};

export default HomeAttributeIconBar;
